/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';

import { Stack, Typography } from '@mui/material';
import { FileDownloadOutlined } from '@mui/icons-material';

import { ArtworkContext } from 'contexts/ArtworkContext';
import { useAutocompleteProperties } from 'hooks/useAutocompleteProperties';
import { getProperty, listAllAutocompleteProperties, listAllAutocompletePropertiesNext } from 'store/slices/properties';

import { DrawerContent } from 'components/common/drawer/DrawerContent';
import { LoadingButton } from 'components/common/LoadingButton';
import { FormAutocompleteField } from 'components/common/form/Form';
import { FormProvider, useForm } from 'react-hook-form';

export const SaveArtwork = () => {
  const dispatch = useDispatch();

  const { callbacks, selectedProperty } = useContext(ArtworkContext);
  const { handleSaveDocument, handleExportDocument, handleDownloadDocument } = callbacks;

  const loadingSave = useSelector((state) => state.artworks.saveDocumentLoading);
  const loadingExport = useSelector((state) => state.artworks.exportDocumentLoading);
  const loadingDownload = useSelector((state) => state.artworks.downloadingArtwork);

  const [searchParams] = useSearchParams();
  const defaultPropertyId = searchParams.get('property');
  const { id: pathPropertyId } = useParams();
  const propertyId = pathPropertyId || defaultPropertyId;

  const { loading, allProperties, offset, hasMore } = useAutocompleteProperties();
  const { setSelectedProperty } = callbacks;

  const methods = useForm({
    defaultValues: {
      property: {},
    },
  });

  const formSelectedProperty = methods.watch('property');

  useEffect(() => {
    if (defaultPropertyId) {
      dispatch(getProperty({ id: defaultPropertyId }))
        .unwrap()
        .then((data) => setSelectedProperty(data));
    }
  }, [defaultPropertyId]);

  useEffect(() => {
    if (selectedProperty?.id) {
      methods.reset({ property: selectedProperty });
    } else {
      methods.reset({ property: {} });
    }
  }, [selectedProperty, methods]);

  return (
    <DrawerContent>
      <DrawerContent.Header title="Export" />
      <DrawerContent.Wrapper>
        <DrawerContent.Content>
          <FormProvider {...methods}>
            <Stack spacing={2}>
              <Stack
                direction="column"
                alignItems="center"
                sx={{ py: 1 }}
              >
                <FileDownloadOutlined sx={{ fontSize: '100px' }} />
              </Stack>
              {!propertyId && (
                <Stack spacing={1}>
                  <FormAutocompleteField
                    name="property"
                    label="Select Property"
                    control={methods.control}
                    onChange={(property) => {
                      if (property) {
                        setSelectedProperty(property);
                        return;
                      }
                      setSelectedProperty(null);
                      dispatch(listAllAutocompleteProperties({ query: '' }));
                    }}
                    optionsData={allProperties}
                    optionName="name"
                    loading={loading}
                    hasMore={hasMore}
                    backendSearch={(query) => dispatch(listAllAutocompleteProperties({ query }))}
                    fetchData={(query) => dispatch(listAllAutocompletePropertiesNext({ offset, query }))}
                  />
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 400, fontSize: '12px' }}
                  >
                    Select a property here to be exported
                  </Typography>
                </Stack>
              )}
              <LoadingButton
                title="SAVE"
                loading={loadingSave}
                toggleClick={() => handleSaveDocument()}
                disabled={loadingExport || loadingDownload || !formSelectedProperty?.id}
                variant="contained"
                style={{
                  backgroundColor: (theme) => theme.palette.primary.main,
                  color: (theme) => theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.primary.main,
                  },
                }}
              />
              <LoadingButton
                title="SAVE AND EXPORT"
                loading={loadingExport}
                toggleClick={() => handleExportDocument()}
                disabled={loadingSave || loadingDownload || !formSelectedProperty?.id}
                variant="contained"
                style={{
                  backgroundColor: (theme) => theme.palette.primary.light,
                  color: (theme) => theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.primary.light,
                  },
                }}
              />
              <LoadingButton
                title="SAVE, EXPORT AND DOWNLOAD"
                loading={loadingDownload}
                toggleClick={() => handleDownloadDocument()}
                disabled={loadingSave || loadingExport}
                style={{
                  border: (theme) => `1px solid ${theme.palette.secondary.dark}`,
                  color: (theme) => theme.palette.secondary.dark,
                  '&:hover': {
                    border: (theme) => `1px solid ${theme.palette.secondary.dark}`,
                    backgroundColor: (theme) => theme.palette.secondary.lighter,
                  },
                }}
              />
            </Stack>
          </FormProvider>
        </DrawerContent.Content>
      </DrawerContent.Wrapper>
    </DrawerContent>
  );
};
